import { Fragment, useEffect, useMemo, useState } from 'react';
import { Button } from '@schibsted-svp/react-ui';
import { Link } from 'react-router-dom';

import { Table } from '../Table/Table';
import { fetchTags } from '../../services/api/tags';

import { TagRow } from './TagRow/TagRow';

import type { CnpTag, EnrichmentResponse, UserResponse } from '../../types';

import styles from './TagsTable.module.scss';

type EnrichmentWithTag = EnrichmentResponse & {
  tag: CnpTag | { title: string };
  updatedBy?: string;
};

type Props = {
  users: UserResponse[];
  enrichments: EnrichmentResponse[];
  loading?: boolean;
};

function TagsTable({ users, enrichments, loading }: Props) {
  const [filter, setFilter] = useState('');
  const [items, setItems] = useState<EnrichmentWithTag[]>([]);

  useEffect(() => {
    if (!enrichments.length) {
      return;
    }

    fetchTags({ ids: enrichments.map((enrichment) => enrichment.tagId) })
      .then((tags) =>
        setItems(
          enrichments.map((enrichment) => ({
            ...enrichment,
            tag: tags.find((tag) => tag.id === enrichment.tagId) ?? {
              title: '«missing tag»',
            },
          }))
        )
      )
      .catch(console.error);
  }, [enrichments]);

  const filtered = useMemo(
    () =>
      items.filter((item) =>
        item.tag.title.toLowerCase().includes(filter.toLowerCase())
      ),
    [items, filter]
  );

  return (
    <Fragment>
      <Table
        items={[...filtered]}
        renderHeader={() => (
          <tr>
            <th>Tag</th>
            <th></th>
            <th className={styles.updated}>Last update</th>
          </tr>
        )}
        renderRow={(enrichment) => (
          <TagRow
            user={users.find(({ id }) => id === enrichment.updatedBy)}
            enrichment={enrichment as EnrichmentResponse & { tag: CnpTag }}
            key={enrichment.tagId}
          />
        )}
        renderActions={() => (
          <Link to="/tags/new">
            <Button type="button">Create a new tag enrichment</Button>
          </Link>
        )}
        onFilterChange={setFilter}
        loading={loading}
      />
    </Fragment>
  );
}

export { TagsTable };
